import { useRoute } from 'vue-router';
import { trackEvent, TrackingEventType } from '@/services/tracking.service';
import { useModuleStore } from '@/stores/module.store';
import type { AccessPolicy } from '@/entities/role.entity';

interface UseTracking {
  trackPageView: (pagePath: string, referrerPath?: string) => void;
  trackComponentInteraction: (
    eventType: TrackingEventType,
    componentName: string,
    metadata?: Record<string, unknown>,
  ) => void;
}

/** Resolves module_id from route's accessPolicy.moduleName via module store. */
const resolveModuleId = (route: ReturnType<typeof useRoute>): string | null => {
  const accessPolicy = route.meta?.accessPolicy as AccessPolicy | undefined;
  if (!accessPolicy?.moduleName) return null;

  const moduleStore = useModuleStore();
  return moduleStore.getModuleIdByName(accessPolicy.moduleName);
};

export const useTracking = (): UseTracking => {
  const route = useRoute();

  const trackPageView = (pagePath: string, referrerPath?: string): void => {
    trackEvent({
      event_type: TrackingEventType.PAGE_VIEW,
      module_id: resolveModuleId(route),
      page_path: pagePath,
      referrer_path: referrerPath,
    });
  };

  const trackComponentInteraction = (
    eventType: TrackingEventType,
    componentName: string,
    metadata?: Record<string, unknown>,
  ): void => {
    trackEvent({
      event_type: eventType,
      module_id: resolveModuleId(route),
      page_path: route.fullPath,
      component_name: componentName,
      metadata,
    });
  };

  return { trackPageView, trackComponentInteraction };
};
